import { Component, NgZone, OnInit } from '@angular/core';
import { HttpService } from 'app/services/http.service';
import { ConfirmationService, MessageService } from 'primeng/api';
import * as XLSX from 'xlsx';

/**
 * Conector MCP, pantalla única: cuánto suma y quién lo usa.
 *
 * Nació partida en dos —"Conector MCP" para emitir y revocar keys, "Adopción MCP"
 * para el total— y se juntó de vuelta: eran dos entradas del menú con los mismos
 * datos vistos de dos formas, y había que saber de antemano en cuál de las dos
 * estaba lo que uno buscaba.
 *
 * Tres números arriba que no hay que confundir:
 *
 *   - ADHERIDOS: empresas con la suscripción activa. Son los que se facturan,
 *     porque aceptaron el servicio.
 *   - CON KEY Y SIN SUSCRIPCIÓN: tienen el conector andando y nunca aceptaron
 *     nada. No suman plata —cobrarles sería facturar sin consentimiento— pero
 *     cada uno es una conversación pendiente, así que se muestran aparte.
 *   - FACTURABLE: los adheridos menos los que todavía están en su período
 *     gratuito. Es lo que se emitiría este mes; el resto entra cuando cada
 *     promoción termine.
 *
 * El monto es una PROYECCIÓN mensual sobre las instalaciones vivas de hoy, no una
 * factura: se cobra por las instalaciones que tenga cada empresa en el mes, así
 * que dar de alta o de baja una lo mueve. Y mientras `CobroVigente` sea false no
 * se está cobrando nada todavía.
 *
 * De acá NO se emiten keys, a propósito: las emite el cliente desde su propio
 * portal, que es donde acepta la suscripción. Que el panel interno pudiera
 * fabricar una key para cualquier empresa era el atajo de cuando esto no existía
 * todavía, y significaba entregar acceso a los datos de un cliente sin que él lo
 * pidiera ni lo supiera. Revocar sí queda: es la salida de emergencia, y cortar
 * un acceso nunca es lo peligroso.
 *
 * El filtro de empresa de arriba NO se le manda al endpoint de adopción: los
 * totales son siempre el total. Filtra la tabla de keys y la bitácora.
 */
@Component({
  selector: 'app-mcpadopcion',
  templateUrl: './mcpadopcion.component.html',
  styleUrls: ['./mcpadopcion.component.scss']
})
export class McpadopcionComponent implements OnInit {

  cargando: boolean = false;

  totales: any = null;
  tarifa: any = null;
  alcance: any = null;
  empresasAdopcion: any[] = [];
  keys: any[] = [];

  // Empresas del sistema, para el filtro de arriba.
  empresas: any[] = [];
  empresaSeleccionada: string = '';

  dias: number = 30;
  rangos: any[] = [
    { label: 'Últimos 7 días', value: 7 },
    { label: 'Últimos 30 días', value: 30 },
    { label: 'Últimos 90 días', value: 90 },
    { label: 'Último año', value: 365 }
  ];

  // Las que no tienen key ni suscripción no aparecen: el endpoint solo devuelve
  // empresas que tocaron el conector alguna vez.
  soloSinSuscripcion: boolean = false;

  // Bitácora
  uso: any[] = [];
  sugerencias: any[] = [];
  llamadas: any[] = [];
  llamadasTotal: number = 0;
  llamadasLimite: number = 0;
  cargandoBitacora: boolean = false;

  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private http: HttpService,
    private ngZone: NgZone,
  ) { }

  async ngOnInit(): Promise<void> {
    await this.getEmpresas();
    await this.carga();
    await this.getBitacora();
  }

  async getEmpresas(): Promise<void> {
    const respuesta = await this.http.getEmpresaService();
    this.empresas = (respuesta && respuesta.empresas) ? respuesta.empresas : (respuesta || []);
    this.ngZone.run(() => { });
  }

  async carga(): Promise<void> {
    this.cargando = true;

    const respuesta = await this.http.getMcpAdopcionService(this.dias);

    this.totales = (respuesta && respuesta.totales) ? respuesta.totales : null;
    this.tarifa = (respuesta && respuesta.tarifa) ? respuesta.tarifa : null;
    this.alcance = (respuesta && respuesta.alcance) ? respuesta.alcance : null;
    this.empresasAdopcion = (respuesta && respuesta.empresas) ? respuesta.empresas : [];
    this.keys = (respuesta && respuesta.keys) ? respuesta.keys : [];

    this.cargando = false;
    // El target de este proyecto es esnext, así que lo que sigue a un await queda
    // fuera de la zona de Angular y la vista no se entera de que cambiaron los
    // datos. Ver el resto de las pantallas: todas terminan igual.
    this.ngZone.run(() => { });
  }

  async getBitacora(): Promise<void> {
    this.cargandoBitacora = true;

    const respuesta = await this.http.getMcpSolicitudesService(this.dias, this.empresaSeleccionada);
    this.uso = (respuesta && respuesta.uso) ? respuesta.uso : [];
    this.sugerencias = (respuesta && respuesta.sugerencias) ? respuesta.sugerencias : [];
    this.llamadas = (respuesta && respuesta.llamadas) ? respuesta.llamadas : [];
    this.llamadasTotal = (respuesta && respuesta.llamadasTotal) ? respuesta.llamadasTotal : 0;
    this.llamadasLimite = (respuesta && respuesta.llamadasLimite) ? respuesta.llamadasLimite : 0;

    this.cargandoBitacora = false;
    this.ngZone.run(() => { });
  }

  // Las llamadas tal como están en la tabla, para mirarlas con calma o
  // conversarlas con el cliente. Solo lo cargado: el endpoint trae las 500 más
  // recientes, y si hay más la pantalla ya lo avisa.
  descargarLlamadas(): void {
    const dosDigitos = (n: number) => String(n).padStart(2, '0');
    const RESULTADOS = { ok: 'ok', vacio: 'sin datos', error: 'error' };

    const filas = this.llamadas.map(l => {
      const f = new Date(l.FechaHora);
      return {
        Fecha: `${dosDigitos(f.getDate())}-${dosDigitos(f.getMonth() + 1)}-${f.getFullYear()}`,
        Hora: `${dosDigitos(f.getHours())}:${dosDigitos(f.getMinutes())}:${dosDigitos(f.getSeconds())}`,
        Empresa: (l.Empresa && l.Empresa.NombreEmpresa) || '',
        Usuario: l.UsuarioNombre || '',
        Herramienta: l.Herramienta,
        Argumentos: l.Argumentos || '',
        Resultado: RESULTADOS[l.Resultado] || l.Resultado,
        Mensaje: l.Mensaje || '',
        Filas: l.Filas != null ? l.Filas : '',
        'Tiempo (ms)': l.DuracionMs
      };
    });

    const hoja = XLSX.utils.json_to_sheet(filas);
    hoja['!cols'] = [12, 10, 36, 22, 28, 70, 12, 50, 8, 12].map(wch => ({ wch }));
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, 'Llamadas');

    const empresa = this.empresas.find(e => e._id === this.empresaSeleccionada);
    const sufijo = empresa ? '_' + empresa.NombreEmpresa.replace(/[^A-Za-z0-9]+/g, '_') : '';
    XLSX.writeFile(libro, `llamadas_mcp${sufijo}_${this.dias}dias.xlsx`);
  }

  async cambiaPeriodo(): Promise<void> {
    await this.carga();
    await this.getBitacora();
  }

  // El filtro de empresa no toca los totales: la adopción es siempre el total.
  // Solo se recarga la bitácora, que sí se pide filtrada al servidor.
  async cambiaEmpresa(): Promise<void> {
    await this.getBitacora();
  }

  get empresasVisibles(): any[] {
    if (!this.soloSinSuscripcion) return this.empresasAdopcion;
    return this.empresasAdopcion.filter(e => !e.Suscrita && e.KeysActivas > 0);
  }

  get keysVisibles(): any[] {
    if (!this.empresaSeleccionada) return this.keys;
    return this.keys.filter(k => k.Empresa && k.Empresa._id === this.empresaSeleccionada);
  }

  // Solo las que de verdad están usando el conector. Una empresa con la key
  // emitida y cero llamadas es un cliente al que hay que ir a acompañar, no un
  // usuario del servicio.
  get empresasActivas(): number {
    return this.empresasAdopcion.filter(e => e.Llamadas > 0).length;
  }

  get keysUsadas(): number {
    return this.keys.filter(k => k.Activa && k.Llamadas > 0).length;
  }

  uf(valor: number): string {
    return (valor || 0).toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' UF';
  }

  pesos(valor: number): string {
    return '$' + (valor || 0).toLocaleString('es-CL');
  }

  /**
   * Chrome ofrece las direcciones que tiene guardadas encima del filtro del
   * dropdown: lo lee como un campo de correo y tapa las opciones reales. El
   * autocomplete="off" que trae PrimeNG en ese input no alcanza —Chrome lo
   * ignora para el autocompletado de direcciones—, pero sí respeta
   * "new-password". El input solo existe mientras el panel está abierto, así
   * que hay que marcarlo cada vez que se muestra.
   */
  apagaAutocompletado(): void {
    const filtro = document.querySelector('.p-dropdown-panel .p-dropdown-filter') as HTMLInputElement;
    if (!filtro) return;

    filtro.setAttribute('autocomplete', 'new-password');
    // Gestores de contraseñas de terceros miran sus propios atributos.
    filtro.setAttribute('data-lpignore', 'true');
    filtro.setAttribute('data-1p-ignore', 'true');
  }

  // --------------------------------------------------------------- revocar

  revoca(key: any): void {
    this.confirmationService.confirm({
      message: 'La key ' + key.Prefijo + ' deja de servir de inmediato y no se puede reactivar. Quien la esté usando pierde el acceso al instante. ¿Revocarla?',
      header: 'Revocar API Key',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, revocar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.http.revocarMcpKeyService(key._id, 'Revocada desde la Web Admin').subscribe(async () => {
          this.messageService.add({ severity: 'success', summary: 'Revocada', detail: 'La key dejó de servir.' });
          await this.carga();
          this.ngZone.run(() => { });
        }, (error: any) => {
          this.messageService.add({
            severity: 'error',
            summary: 'No se pudo revocar',
            detail: (error.error && error.error.error) ? error.error.error : 'Error inesperado'
          });
          this.ngZone.run(() => { });
        });
      }
    });
  }
}
